import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

/// The colour a picture leaves in the room. Read from the image itself rather than sent by the
/// server: it is one canvas and a thousand pixels, and it means a poster tints the page it is
/// on without anything having to be stored about it.
///
/// Returns undefined until there is an answer, and stays undefined when there is no picture or
/// no colour worth using - the caller then falls back to its own background rather than to a
/// colour nobody chose.
///
/// What comes back is the picture's hue at the lightness the interface is currently painted
/// in: the same poster tints a dark page with a deep wash and a light one with a pale one.
export function useDominantColour(url: string | undefined): string | undefined {
  const theme = useTheme();

  // The address is kept with the colour, so the answer for the last picture is never shown
  // over the next one. Hue and saturation rather than a finished colour, because the
  // lightness belongs to the mode and the mode can change without the picture changing.
  const [resolved, setResolved] = useState<{ url: string; hue: number; saturation: number }>();

  useEffect(() => {
    if (!url) {
      return undefined;
    }

    let cancelled = false;
    const image = new Image();

    image.onload = () => {
      if (cancelled) {
        return;
      }

      try {
        const colour = dominantColour(image);

        if (colour) {
          setResolved({ url, ...colour });
        }
      } catch {
        // A canvas the browser refuses to read from is not worth a broken page.
      }
    };

    image.src = url;

    return () => {
      cancelled = true;
      image.onload = null;
    };
  }, [url]);

  if (!resolved || resolved.url !== url) {
    return undefined;
  }

  // A wash, not a filter: the picture's own hue, held to a lightness the page's text stays
  // readable on, and kept further off full saturation in daylight where a strong colour
  // behind black text is harder to read than behind white.
  const dark = theme.palette.mode === 'dark';
  const saturation = Math.min(resolved.saturation, dark ? 0.65 : 0.5);

  return `hsl(${Math.round(resolved.hue)} ${Math.round(saturation * 100)}% ${dark ? 20 : 86}%)`;
}

/// How many shades each channel is sorted into. Sixteen puts every red in one bucket while
/// still telling a red apart from an orange.
const LEVELS = 16;

/// Below this, a pixel is shadow; above it, it is a highlight or the white of a title. Neither
/// says anything about what colour the picture is.
const MINIMUM_LIGHTNESS = 0.12;
const MAXIMUM_LIGHTNESS = 0.94;

/// The colour a picture is actually made of, rather than the average of it. An average mixes
/// everything into a muddy middle - a red poster on a dark ground averages to a grey-green
/// that appears nowhere in the picture. So the pixels are sorted into bins, and the bin that
/// wins on how much of the picture it covers and how colourful it is decides.
function dominantColour(image: HTMLImageElement): { hue: number; saturation: number } | undefined {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return undefined;
  }

  context.drawImage(image, 0, 0, size, size);

  const { data } = context.getImageData(0, 0, size, size);
  const bins = new Map<number, { count: number; score: number; red: number; green: number; blue: number }>();

  for (let index = 0; index < data.length; index += 4) {
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];

    const { saturation, lightness } = toHsl(red, green, blue);

    if (lightness < MINIMUM_LIGHTNESS || lightness > MAXIMUM_LIGHTNESS) {
      continue;
    }

    const key =
      (Math.floor((red / 256) * LEVELS) * LEVELS + Math.floor((green / 256) * LEVELS)) * LEVELS
      + Math.floor((blue / 256) * LEVELS);

    const bin = bins.get(key) ?? { count: 0, score: 0, red: 0, green: 0, blue: 0 };

    bin.count += 1;

    // Area alone would hand every page to the dark grey that most pictures are mostly made of.
    // Colour alone would hand it to a single bright pixel. The product is the compromise.
    bin.score += 0.25 + saturation;
    bin.red += red;
    bin.green += green;
    bin.blue += blue;

    bins.set(key, bin);
  }

  let winner: { count: number; score: number; red: number; green: number; blue: number } | undefined;

  for (const bin of bins.values()) {
    if (!winner || bin.score > winner.score) {
      winner = bin;
    }
  }

  if (!winner) {
    return undefined;
  }

  const { hue, saturation } = toHsl(
    winner.red / winner.count,
    winner.green / winner.count,
    winner.blue / winner.count,
  );

  // A picture with no colour in it gets no wash at all.
  if (saturation < 0.12) {
    return undefined;
  }

  return { hue, saturation };
}

function toHsl(red: number, green: number, blue: number): { hue: number; saturation: number; lightness: number } {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { hue: 0, saturation: 0, lightness };
  }

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));

  let hue: number;

  if (max === r) {
    hue = ((g - b) / delta) % 6;
  } else if (max === g) {
    hue = (b - r) / delta + 2;
  } else {
    hue = (r - g) / delta + 4;
  }

  hue *= 60;

  return { hue: hue < 0 ? hue + 360 : hue, saturation, lightness };
}
